import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import Details from './Details';
import InboundLinks from './InboundLinks';
import Changes from './Changes';
import { formatDisplayDate, usePushParams } from './commons';
import { CountBadge } from './Pills';
import { ERROR_404 } from '../../reducers/currentMonitoredUri';

export default function Tabs({ values, uri, internalAhref, hasChanges, changesCount, tab, onTabChange }) {
    const { curSnapshotDate, lastSnapshotDate } = useSelector(s => s.currentMonitoredUri);
    const pushParams = usePushParams();
    const isLatest = curSnapshotDate && lastSnapshotDate && curSnapshotDate === lastSnapshotDate;
    const isPast = curSnapshotDate && lastSnapshotDate && curSnapshotDate !== lastSnapshotDate;
    const goLatest = () => pushParams({ date: null, from: null, to: null });

    return (
        <Card $past={isPast}>
            <TabBar>
                <Tab $active={tab === 'details'} onClick={() => onTabChange('details')}>Details</Tab>
                <Tab $active={tab === 'inbound'} onClick={() => onTabChange('inbound')}>
                    Inbound Links
                    {internalAhref !== '-' && <CountBadge>{internalAhref}</CountBadge>}
                </Tab>
                <Tab $active={tab === 'changes'} $disabled={!hasChanges} title={!hasChanges ? 'No changes detected for this crawl' : undefined} onClick={() => hasChanges && onTabChange('changes')}>
                    Changes
                    {hasChanges && <CountBadge $solid>{changesCount}</CountBadge>}
                </Tab>
            </TabBar>
            {isPast && (
                <PastBar>
                    <PastBarText>
                        {values.cur.fetchStatus === ERROR_404
                            ? <>There is no crawl for <strong>{formatDisplayDate(curSnapshotDate)}</strong>.</>
                            : <>You're viewing a previous crawl from <strong>{formatDisplayDate(curSnapshotDate)}</strong> — this may not reflect the current state of this page.</>}{' '}
                        <GoLatestLink onClick={goLatest}>Go to latest</GoLatestLink>
                    </PastBarText>
                    <PastBarClose onClick={goLatest} title="Go to latest">✕</PastBarClose>
                </PastBar>
            )}
            {isLatest && (
                <SnapshotBannerGreen>
                    Latest crawl · <strong>{formatDisplayDate(curSnapshotDate)}</strong>
                </SnapshotBannerGreen>
            )}
            {tab === 'details' && <Details values={values} onGoInbound={() => onTabChange('inbound')} />}
            {tab === 'inbound' && <InboundLinks uri={uri} isVisible inboundLinksCount={internalAhref} />}
            {tab === 'changes' && <Changes />}
        </Card>
    );
}

const Card = styled.div`
    background: #fff;
    border: 2px solid ${({ $past }) => $past ? '#f59e0b' : '#e2e8f0'};
    border-radius: 12px;
    overflow: hidden;
    @media (max-width: 600px) { border-radius: 0; border-left: none; border-right: none; }
`;

const TabBar = styled.div`
    display: flex;
    border-bottom: 1px solid #e2e8f0;
    padding: 0 4px;
`;

const Tab = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    font-size: 0.78rem;
    @media (max-width: 480px) { padding: 10px 12px; font-size: 0.72rem; }
    font-weight: ${({ $active }) => $active ? 700 : 500};
    color: ${({ $active, $disabled }) => $active ? '#6366f1' : $disabled ? '#cbd5e1' : '#64748b'};
    background: none;
    border: none;
    border-bottom: 2px solid ${({ $active }) => $active ? '#6366f1' : 'transparent'};
    cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
    margin-bottom: -1px;
    transition: color 120ms;
    &:hover { color: ${({ $disabled }) => $disabled ? '#cbd5e1' : '#6366f1'}; }
`;


const PastBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px 9px 20px;
    background: #fefce8;
    border-bottom: 1px solid #fde68a;
    font-family: "Inter", system-ui, sans-serif;
    @media (max-width: 600px) { padding: 8px 8px 8px 14px; }
`;

const PastBarText = styled.span`
    flex: 1;
    font-size: 0.78rem;
    color: #92400e;
`;

const GoLatestLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: inherit;
    font-weight: 700;
    color: #92400e;
    text-decoration: underline;
    cursor: pointer;
    &:hover { color: #78350f; }
`;

const PastBarClose = styled.button`
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 5px;
    font-size: 0.8rem;
    color: #b45309;
    cursor: pointer;
    line-height: 1;
    transition: background 120ms, color 120ms;
    &:hover { background: #fde68a; color: #78350f; }
`;

const SnapshotBannerGreen = styled.div`
    padding: 8px 20px;
    background: #eff6ff;
    border-bottom: 1px solid #bfdbfe;
    font-size: 0.78rem;
    color: #1d4ed8;
`;


